import type * as Theme from '#theme'
import type { Image } from '../types/media'
import { resolveThemeAssetImage } from '../theme/utils/resolveThemeAsset'
import { themeAssetUrls } from '#theme-assets'
import { computed, useTheme } from '#imports'
import type { ComputedRef } from '#imports'

/**
 * Resolves a brand illustration for the given slot.
 *
 * Resolution: `theme.illustration[slot]` only. Unlike `useThemeLogo` and
 * `useThemeFavicon` there is no `site.illustration` consumer override —
 * illustrations are theme-owned to keep brand expressiveness centralised.
 *
 * Theme-shipped illustrations are declared with brand-relative keys
 * (e.g. `spilnu/illustration/success.png`) and resolved to hashed URLs via
 * the Vite-generated `#theme-assets` map.
 *
 * Returns `undefined` when the active theme doesn't define the slot. Call
 * sites in core ship a literal `?? '/media/<legacy>.png'` fallback so
 * downstream consumer apps whose themes omit a slot still render an image
 * — the in-core themes (spilnu / spinking / luxcasino) define every slot,
 * so for them the fallback branch is unreachable. Once every consumer
 * theme populates the vocabulary, the fallbacks can be dropped.
 *
 * @example
 * const success = useThemeIllustration('success')
 * // -> ComputedRef<Image | undefined>
 * // <img :src="success.value?.src" :width="success.value?.width">
 */
export function useThemeIllustration(slot: Theme.IllustrationSlot): ComputedRef<Image | undefined> {
    const { theme } = useTheme()

    return computed(() => {
        const raw = theme.value.illustration?.[slot]
        return resolveThemeAssetImage(raw, themeAssetUrls)
    })
}
